import { useState, useEffect, useCallback, useRef } from 'react';

export interface PaginatedRequest {
  search?: string;
  page: number;
  limit: number;
  filterType?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  hasNext?: boolean;
  total: number;
  pageCount?: number;
}

interface UsePaginatedDropdownProps<T> {
  fetchFn: (params: PaginatedRequest) => Promise<PaginatedResponse<T>>;
  initialLimit?: number;
  initialSearch?: string;
  filterType?: string;
}

const MAX_SELECT_ALL_PAGES = 500;

export function usePaginatedDropdown<T>({
  fetchFn,
  initialLimit = 50,
  initialSearch = '',
  filterType,
}: UsePaginatedDropdownProps<T>) {
  const requestInProgress = useRef(false);
  const pageLock = useRef(false);
  const hasMoreRef = useRef(true);
  const fetchAllInProgress = useRef(false);
  const dataRef = useRef<T[]>([]);
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Total results from bacend
  const [total, setTotal] = useState(0);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  /**
   * LOAD DATA
   */
  const loadData = useCallback(async () => {
    if (fetchAllInProgress.current) return;
    if (requestInProgress.current) return;
    if (!hasMoreRef.current && page !== 1) return; //extra check to stop unnecesary call
    requestInProgress.current = true;
    setLoading(true);

    try {
      const res = await fetchFn({
        page,
        limit: initialLimit,
        search,
        filterType,
      });

      hasMoreRef.current = !!res.hasNext;
      setHasMore(!!res.hasNext);
      setTotal(res.total ?? 0);

      if (page === 1) {
        setData(res?.data ?? []);
      } else {
        setData((prev) => [...prev, ...(res?.data ?? [])]);
      }
    } finally {
      setLoading(false);
      requestInProgress.current = false;
      pageLock.current = false;
    }
  }, [fetchFn, page, search, initialLimit, filterType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * SEARCH (Debounced properly)
   */
  const setSearchQuery = useCallback(() => {
    let timer: number;

    return (value: string) => {
      clearTimeout(timer);

      timer = window.setTimeout(() => {
        setSearch(value);
        setPage(1);
        hasMoreRef.current = true;
        setHasMore(true);
      }, 500);
    };
  }, [])();

  /**
   * LOAD MORE (Bulletproof)
   */
  const loadMore = useCallback(() => {
    if (fetchAllInProgress.current) return;
    if (pageLock.current) return;
    if (requestInProgress.current) return;
    if (!hasMore) return;

    pageLock.current = true;
    setPage((prev) => prev + 1);
  }, [hasMore]);

  /**
   * Fetch every page for the current search and replace local data.
   * Used by Select all so every matching id is selected, not just loaded ones.
   */
  const fetchAllMatching = useCallback(async (): Promise<T[]> => {
    if (fetchAllInProgress.current) return dataRef.current;

    fetchAllInProgress.current = true;
    setLoading(true);

    try {
      let pageNum = 1;
      let all: T[] = [];
      let hasNext = true;

      while (hasNext && pageNum <= MAX_SELECT_ALL_PAGES) {
        const res = await fetchFn({
          page: pageNum,
          limit: initialLimit,
          search,
          filterType,
        });
        const chunk = res?.data ?? [];
        if (pageNum === 1) all = [...chunk];
        else all.push(...chunk);
        hasNext = !!res.hasNext;
        if (!hasNext && res.pageCount && pageNum < res.pageCount) {
          hasNext = true;
        }
        setTotal(res.total ?? 0);
        if (chunk.length === 0) break;
        pageNum += 1;
      }

      setData(all);
      setPage(Math.max(1, pageNum - 1));
      hasMoreRef.current = false;
      setHasMore(false);
      return all;
    } finally {
      setLoading(false);
      fetchAllInProgress.current = false;
      requestInProgress.current = false;
      pageLock.current = false;
    }
  }, [fetchFn, filterType, initialLimit, search]);

  return {
    data,
    search,
    setSearch: setSearchQuery,
    loadMore,
    hasMore,
    loading,
    total,
    fetchAllMatching,
  };
}
